import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { can } from '@kronos/domain'
import type { Role, Section } from '@kronos/domain'
import { repository } from '../../data'
import { EmptyState, PageHeader, Panel } from '../primitives'
import { Tabs, choose } from '../shell/Sections'
import { AccessTab } from './AccessTab'
import { AddMachine } from './AddMachine'
import { GiveAccess } from './GiveAccess'
import { MachinesTab } from './MachinesTab'
import { NoteProvider } from './parts'
import { PeopleTab } from './PeopleTab'
import { StatusStrip } from './StatusStrip'
import { Takeover } from './Takeover'
import { derive } from './derive'

/**
 * Connectivity: who may SSH into which robot or office computer.
 *
 * Admins only — SSH on a robot is root and reaches the code. Access is given
 * team × group (Access), every machine shows who can reach it and why
 * (Machines), and every person shows what they can reach (People). Kronos
 * builds the whole network policy from this and pushes it; an edit made on the
 * network server directly is put back within a minute.
 */
export function ConnectivityPlace({ roles, section }: { roles: readonly Role[]; section: Section | null }): JSX.Element {
  const allowed = can(roles, 'manage_network_access')
  const { sections, current } = choose(roles, 'connectivity', section)
  const [modal, setModal] = useState<'machine' | 'direct' | null>(null)
  const q = useQuery({ queryKey: ['connectivity'], queryFn: () => repository.connectivity(), enabled: allowed, refetchInterval: 30_000 })
  const view = useMemo(() => (q.data ? derive(q.data) : null), [q.data])

  const header = (
    <PageHeader
      title="Connectivity"
      subtitle="Who can SSH into which robot or office computer."
      actions={
        view?.page.takenOver ? (
          <>
            <button onClick={() => setModal('direct')}>Give access</button>
            <button className="primary" onClick={() => setModal('machine')}>
              Add a machine
            </button>
          </>
        ) : undefined
      }
    />
  )

  if (!allowed || current === null) {
    return (
      <div className="page">
        {header}
        <Panel>
          <EmptyState title="Only an admin decides who can SSH into machines">
            SSH on a robot is effectively root and reaches the code, so it sits with the people who hand out roles.
          </EmptyState>
        </Panel>
      </div>
    )
  }

  let body: React.ReactNode
  if (q.isPending) body = <p className="muted">Loading…</p>
  else if (q.isError || !view) body = <p className="critical">Could not load Connectivity.</p>
  else if (!view.page.connected) {
    body = (
      <Panel>
        <EmptyState title="Kronos is not connected to the network server">Set HEADSCALE_URL and an API key for the API, then reload.</EmptyState>
      </Panel>
    )
  } else if (!view.page.takenOver) body = <Takeover />
  else {
    body = (
      <>
        <StatusStrip view={view} />
        <Tabs hub="connectivity" sections={sections} current={current} counts={{ machines: view.machines.length, people: view.people.length }} />
        {current === 'access' ? <AccessTab view={view} /> : null}
        {current === 'machines' ? <MachinesTab view={view} /> : null}
        {current === 'people' ? <PeopleTab view={view} /> : null}
      </>
    )
  }

  return (
    <NoteProvider>
      <div className="page connectivity">
        {header}
        {body}
        {modal === 'machine' && view ? (
          <AddMachine robots={view.page.robots} taken={view.page.world.machines.map((m) => m.tag)} onClose={() => setModal(null)} />
        ) : null}
        {modal === 'direct' && view ? <GiveAccess view={view} onClose={() => setModal(null)} /> : null}
      </div>
    </NoteProvider>
  )
}
